import { Address, getAddress, zeroAddress } from "viem"
import { ContractFunctionResult } from "../evc"
import { BaseOracleNamesAndQuotes, OracleBatchItemFactory } from "../oracles"

import { contractBook } from "@eulerxyz/core-sdk"
import { parseState } from "../utils"

type ContractResult = ContractFunctionResult<
  typeof contractBook.vaultLens.abi,
  "getVaultInfoFull"
>

type EulerEarnVaultResult = ContractFunctionResult<
  typeof contractBook.eulerEarnVaultLens.abi,
  "getVaultInfoFull"
>

export type VaultCreator = {
  name: string
  logo?: string
  description?: string
  url?: string
  addresses?: Record<Address, string | undefined>
  social?: {
    twitter?: string
    youtube?: string
    discord?: string
    telegram?: string
    github?: string
  }
}

export type AssetMeta = {
  doesNotAutoCompound?: boolean
  isPendlePT?: boolean
  pendleMarket?: string
  poolId?: string
  claimUrl?: string
}

type TokenValues = {
  assetName: string
  assetSymbol: string
  assetMeta?: AssetMeta
  unitOfAccountName: string
  unitOfAccountSymbol: string
  nameOverride?: boolean
  vaultCreator?: VaultCreator
}

export type ResultVaultAggregator = ContractResult & {
  baseOracle?: BaseOracleNamesAndQuotes
} & TokenValues

export type ResultEulerEarnVaultAggregator = EulerEarnVaultResult & {
  baseOracle?: BaseOracleNamesAndQuotes
} & TokenValues

export type PendingResolve = (value: ResultVaultAggregator | null) => void

export type VaultType = "classic" | "managed"

export class VaultAggregator<TResult> {
  #pendingResolves: ((value: TResult | null) => void)[] = []
  #pendingRejects: ((error: Error) => void)[] = []
  #pendingVaults: Address[] = []
  #timer?: ReturnType<typeof setTimeout> = undefined
  #chainId: number
  #oracleBatchItemFactory: OracleBatchItemFactory
  #type: VaultType
  constructor(chainId: number, type: VaultType = "classic") {
    this.#chainId = chainId
    this.#oracleBatchItemFactory = new OracleBatchItemFactory(chainId)
    this.#type = type
  }

  getVault(address: Address): Promise<TResult | null> {
    this.scheduleBatch()

    this.#pendingVaults.push(address)

    return new Promise<TResult | null>((resolve, reject) => {
      this.#pendingResolves.push(resolve)
      this.#pendingRejects.push(reject)
    })
  }
  protected scheduleBatch() {
    if (this.#timer === undefined) {
      this.#timer = setTimeout(async () => {
        this.#timer = undefined
        await this.makeBatchNow()
      }, 0)
    }
  }

  protected async makeBatchNow() {
    const pendingResolves = this.#pendingResolves
    const pendingRejects = this.#pendingRejects
    const pendingVaults = this.#pendingVaults
    this.#pendingResolves = []
    this.#pendingRejects = []
    this.#pendingVaults = []

    const batchSize = 150 // Define the batch size
    const vaultBatches = this.splitIntoBatches(pendingVaults, batchSize) // Split vaults into batches

    try {
      // Process each batch
      const batchResponses: Record<Address, TResult>[] = await Promise.all(
        vaultBatches.map(
          (vaultBatch) => this.fetchVaultsBatch(vaultBatch), // Fetch each batch
        ),
      )

      // Merge batch responses
      const mergedResponse = Object.assign({}, ...batchResponses)

      if (!Object.keys(mergedResponse).length) {
        throw new Error("No vaults found")
      }

      const response = parseState(mergedResponse)

      for (const [i, address] of pendingVaults.entries()) {
        const pendingResolve = pendingResolves[i]
        const pendingReject = pendingRejects[i]

        if (!pendingResolve || !pendingReject) {
          throw new Error("No resolve or reject function found")
        } else {
          const vault = response[getAddress(address)]
          if (vault) {
            const data: TResult = {
              ...vault,
              // Only the classic vaults have oracleInfo stuff
              ...(this.#type === "classic" &&
                vault.oracleInfo.oracle !== zeroAddress && {
                  baseOracle:
                    this.#oracleBatchItemFactory.getBaseOracleNamesAndQuotes({
                      ...vault.oracleInfo,
                      vault: typeof vault === "string" ? vault : vault.vault,
                    }),
                }),
            }
            pendingResolve(data)
          } else {
            pendingResolve(null)
          }
        }
      }
    } catch (error) {
      for (const pendingReject of pendingRejects) {
        pendingReject(error as Error)
      }
    }
  }

  // Helper function to split vaults into batches
  private splitIntoBatches<Item>(items: Item[], batchSize: number): Item[][] {
    const batches = []
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize))
    }
    return batches
  }

  // Fetch a single batch of vaults
  private async fetchVaultsBatch(
    vaultBatch: Address[],
  ): Promise<Record<Address, TResult>> {
    const query = `vaults=${vaultBatch.join(",")}`

    return fetch(
      `/api/v1/vault?chainId=${this.#chainId}&${query}&type=${this.#type}`,
    )
      .then((res) => res.json())
      .catch((error) => {
        throw new Error(`Failed to fetch vault batch: ${error.message}`)
      })
  }
}
